import React from "react";
import { TextField, Autocomplete, Radio } from "@mui/material";
import { styled } from "@mui/material/styles";
import "react-phone-input-2/lib/style.css";

// Define an interface for the custom prop
interface StyledTextFieldProps {
  highlightRed?: boolean; // Optional boolean prop to control the color
  borderRadius?: string;
  padding?: string;
  fontSize?: string;
}
interface StyledOtpFieldProps {
  width?: string;
}

export const PhoneInputField = ({
  helperText,
  error,
  onChangePhone,
  onChangeCountryCode,
  value,
  code,
  placeholder = "Enter phone number",
  disabled = false,
  isEditable = true,
  countryCode,
  codeHelperText,
  inputProps,
  ...props
}: any) => {
  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const phoneValue = event.target.value;

    // Call parent function to update value
    onChangePhone(phoneValue);
    // Perform validation for phone number length
    if (phoneValue.length > 0 && phoneValue.length < 7) {
      helperText = "Phone number must be at least 7 digits.";
      error = true;
    } else if (phoneValue.length > 15) {
      helperText = "Phone number must not exceed 15 digits.";
      error = true;
    } else {
      helperText = "";
      error = false;
    }
  };

  const handleCountryCodeChange = (
    event: any,
    newValue: { label: string; value: string } | null
  ) => {
    onChangeCountryCode(newValue ? newValue.value : ""); // Update the country code in the parent
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          {/* Searchable Country Code Dropdown */}
          <Autocomplete
            disablePortal
            options={countryCode}
            getOptionLabel={(option: any) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value}
            value={
              countryCode?.find((item: any) => item?.value === code) || null
            }
            onChange={handleCountryCodeChange}
            sx={{
              flex: "0 0 130px",
              fontSize: "13px",
              textTransform: "capitalize !important",
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px", // Border radius
                height: "38px", // Field height
                fontSize: "11px",
                color: "#a5a3ae",
                fontWeight: "400",
              },
            }}
            renderInput={(params) => (
              <TextField
                value={value ?? ""}
                placeholder="Code"
                error={!!codeHelperText} // Show error style if codeHelperText exists
                helperText={codeHelperText} // Display the error message
                {...params}
                variant="outlined"
                size="small"
                style={{
                  flex: "0 0 120px", // Adjust width of the input
                }}
              />
            )}
          />

          {/* Phone Number Input Field */}
          <InputField
            error={!!helperText} // Show error style if helperText exists
            helperText={helperText} // Display the error message
            value={value}
            onChange={handlePhoneChange} // Handle input value and validation
            placeholder={placeholder}
            style={{
              flex: "1", // Takes the remaining space
            }}
            inputProps={{
              maxLength: 15, // Restrict input length to 15 characters
              inputMode: "numeric", // Ensure numeric keyboard is used on mobile devices
              pattern: "[0-9]*", // Only allow digits
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                // Block non-numeric key presses except for navigation and deletion keys
                if (
                  !/[0-9]/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "ArrowLeft" &&
                  e.key !== "ArrowRight" &&
                  e.key !== "Delete"
                ) {
                  e.preventDefault();
                }
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

export const TextAreaField = styled(TextField)<StyledTextFieldProps>(
  ({ highlightRed }) => ({
    "& label.Mui-focused": {
      boxSizing: "none",
      color: "#a5a3ae", // Focused label color
    },
    "& label.Mui-error": {
      color: "#d32f2f", // Error state label color
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "6px",
      "&:hover fieldset": {
        borderColor: "#1CB1B1", // Hover border color
      },
      "&.Mui-error:hover fieldset": {
        borderColor: "#d32f2f", // Hover border color in error state
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1CB1B1", // Focus border color
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f", // Error border color
      },
    },
    "& .MuiInputBase-input": {
      minHeight: "100px", // Minimum height for textarea
      resize: "none", // Disable resize
      lineHeight: "1.5", // Adjust line height for better readability
    },
    "& input.Mui-disabled": {
      color: highlightRed ? "red" : "black", // Highlight red for disabled state, default black otherwise
    },
    "& .MuiFormLabel-root": {
      position: "absolute", // Position the label outside the input field
      left: "12px", // Align the label with the input box
      fontSize: "14px", // Optionally adjust the label font size
      color: "#1CB1B1", // Custom color for label
    },
  })
);

export const InputField = styled(TextField)<StyledTextFieldProps>(
  ({ highlightRed, borderRadius = "6px", padding = "7px 10px 7px 0px", fontSize }) => ({
    "& label.Mui-focused": {
      color: "#1CB1B1",
      background: "#fff",
    },
    "& label.Mui-error": {
      color: "#d32f2f",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#1CB1B1",
      background: "#fff",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: borderRadius,
      fontSize: "13px",
      fontFamily: "Jost",
      fontWeight: "400",
      height: "38px",
      color: "#6f6b7d",
      borderColor: "#00B3B5",
      padding: padding,
      "&.Mui-disabled": {
        color: "rgba(0, 0, 0, 0.38)",
        opacity: 0.6,
      },
      "&.Mui-disabled fieldset": {
        color: "rgba(0, 0, 0, 0.38)",
      },
      "&.Mui-disabled fieldset, &.Mui-disabled:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.38) !important",
      },
      "&:hover fieldset": {
        borderColor: "#1CB1B1",
      },
      "&.Mui-error:hover fieldset": {
        borderColor: "#d32f2f",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#00B3B5", // Remove highlight color when focused
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
      },
    },
    "& input.Mui-disabled": {
      color: highlightRed ? "red" : "black", // Red color even when disabled
    },
    "& .MuiFormLabel-root": {
      position: "absolute", // Position the label outside the input field
      left: "0px", // Align the label with the input box
      fontSize: "11px", // Adjust the label font size
      color: "#5d586c", // Custom color for label
      fontFamily: "Jost",
      fontWeight: "400",
    },
    "& .MuiInputBase-input": {
      boxSizing: "inherit !important",
      fontSize: fontSize ? fontSize : "13px",
      textAlign: "left !important",
    },
    // Hide number input spinner for Chrome, Safari, Edge, Opera
    '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button':
      {
        "-webkit-appearance": "none",
        margin: 0,
      },
    // Hide number input spinner for Firefox
    '& input[type="number"]': {
      "-moz-appearance": "textfield",
    },
    "& .MuiFormLabel-root.Mui-required": {
      color: "#d32f2f !important",
    },
    // You may need to add this for specific browsers/OS
    "& input[type='date']": {
      textAlign: "left !important",
      "&::-webkit-date-and-time-value": {
        textAlign: "left !important",
      },
    },
  })
);

export const InputFieldOTP = styled(TextField)<StyledOtpFieldProps>(
  ({ width }) => ({
    "& label.Mui-focused": {
      color: "#1CB1B1",
      boxSizing: "inherit",
    },
    "& label.Mui-error": {
      color: "#d32f2f",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#1CB1B1",
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "center",
      paddingInline: "0px",
      height: "40px",
      width: width,
      padding: "0px",
    },
    "& .MuiOutlinedInput-root": {
      boxSizing: "inherit",
      borderRadius: "8px",
      border: "0px solid lightgray",
      "&:hover fieldset": {
        borderColor: "#1CB1B1",
      },
      "&.Mui-error:hover fieldset": {
        borderColor: "#d32f2f",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1CB1B1",
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
      },
    },
    // Hide number input spinner for Chrome, Safari, Edge, Opera
    '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button':
      {
        "-webkit-appearance": "none",
        margin: 0,
      },
    // Hide number input spinner for Firefox
    '& input[type="number"]': {
      "-moz-appearance": "textfield",
    },
  })
);

export const RadioInputField = styled(Radio)(({ theme }) => ({
  color: "#00B3B5", // Default color
  "&.Mui-checked": {
    color: "#00B3B5", // Color when checked
  },
  "&.Mui-disabled": {
    color: theme.palette.action.disabled, // Color when disabled (default Material-UI color for disabled elements)
  },
}));

export const TextareaField = styled(TextField)<StyledTextFieldProps>(
  ({ highlightRed, borderRadius = "6px", padding = "7px 10px 7px 0px" }) => ({
    "& label.Mui-focused": {
      color: "#1CB1B1",
    },
    "& label.Mui-error": {
      color: "#d32f2f",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#1CB1B1",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: borderRadius,
      fontSize: "13px",
      fontFamily: "Jost",
      fontWeight: "400",
      height: "100%",
      color: "#6f6b7d",
      borderColor: "#00B3B5",
      padding: padding,
      "&:hover fieldset": {
        borderColor: "#1CB1B1",
      },
      "&.Mui-error:hover fieldset": {
        borderColor: "#d32f2f",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#00B3B5", // Remove highlight color when focused
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
      },
    },
    "& input.Mui-disabled": {
      color: highlightRed ? "red" : "black", // Red color even when disabled
    },
    "& .MuiFormLabel-root": {
      position: "absolute", // Position the label outside the input field
      left: "0px", // Align the label with the input box
      fontSize: "15px", // Adjust the label font size
      color: "#5d586c", // Custom color for label
      fontFamily: "Jost",
      fontWeight: "400",
    },
    "& .MuiInputBase-input": {
      boxSizing: "inherit !important",
    },
  })
);
